import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Plot from 'react-plotly.js';
import FilterBar from '../components/FilterBar';
import CampaignPanel from '../components/CampaignPanel';
import SignalIntelligencePanel from '../components/SignalIntelligencePanel';
import { ClipButton } from '../components/ReportWorkspace';
import '../styles/Analytics.css';
import '../styles/NewDashboard.css';
import filterParams from '../utils/filterParams';
import { qualityPercent } from '../utils/evidenceStatus';

// Topic groups come from the backend (org config `entity_type_groups`).
const isSupplyTopic = (x) => x.topic_group === 'product' || x.topic_group === 'ingredient';
const number = (value) => Number(value || 0);
const rate = (value) => (value == null ? '—' : `${number(value).toFixed(1)}`);
const movement = (value) =>
  value == null ? 'Not comparable' : `${value > 0 ? '+' : ''}${number(value).toFixed(1)} points`;
const score = (value) => (value == null ? '—' : `${qualityPercent(value)}%`);

function Section({ title, description, clip, children }) {
  return (
    <section className="decision-pane analytics-panel">
      <header>
        <div>
          <h3>{title}</h3>
          <p>{description}</p>
        </div>
        {clip && <ClipButton item={clip} />}
      </header>
      {children}
    </section>
  );
}

function TopicTable({ rows, selected, onSelect, label = 'Topic' }) {
  return (
    <div className="analytics-table-wrap">
      <table className="analytics-table">
        <thead>
          <tr>
            <th>{label}</th>
            <th>Province</th>
            <th>Conversations</th>
            <th>Field staff</th>
            <th>Weeks</th>
            <th>Recent / 100</th>
            <th>Change</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={`${row.entity_type}|${row.entity_name}|${row.province}|${index}`}
              className={selected === row ? 'active' : ''}
              onClick={() => onSelect(row)}
            >
              <td>
                <button type="button" onClick={() => onSelect(row)}>
                  {row.entity_name}
                </button>
                <small>{row.entity_type}</small>
              </td>
              <td>{row.province}</td>
              <td>{row.conversations}</td>
              <td>{row.contributors}</td>
              <td>{row.recorded_weeks}</td>
              <td>{rate(row.recent_rate)}</td>
              <td>{movement(row.change_points)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!rows.length && <p className="analytics-empty">No matching recorded topics in this selection.</p>}
    </div>
  );
}

export default function Analytics({ filters, setLoading }) {
  const [tab, setTab] = useState('marketing');
  const [marketingView, setMarketingView] = useState('compare');
  const [data, setData] = useState({
    brands: [],
    companies: [],
    team: [],
    regions: [],
    agents: [],
    assignments: [],
    quality: [],
    territory: { areas: [], activity: [] },
    topics: [],
  });
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [brandName, setBrandName] = useState('');
  const [compareName, setCompareName] = useState('');
  const [agentId, setAgentId] = useState('');
  const [province, setProvince] = useState('');
  const [topicType, setTopicType] = useState('all');
  const [selectedTopic, setSelectedTopic] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    const p = filterParams(filters);
    let active = true;
    setBusy(true);
    setError('');
    setLoading?.(true);
    setSelectedTopic(null);
    Promise.allSettled([
      axios.get(`/api/decision/brands?${p}`, { signal: controller.signal }),
      axios.get(`/api/agent-view/stats?${p}`, { signal: controller.signal }),
      axios.get(`/api/agent-view/insights?${p}`, { signal: controller.signal }),
      axios.get('/api/agent-view/assignments', { signal: controller.signal }),
      axios.get('/api/agent-view/performance', { signal: controller.signal }),
      axios.get(`/api/agent-view/territory?${p}`, { signal: controller.signal }),
      axios.get(`/api/analytics/topics?${p}`, { signal: controller.signal }),
    ])
      .then((results) => {
        if (!active) return;
        const read = (index) => (results[index].status === 'fulfilled' ? results[index].value.data : {});
        const brand = read(0),
          team = read(1),
          insight = read(2),
          assignment = read(3),
          quality = read(4),
          territory = read(5),
          topics = read(6);
        setData({
          brands: brand.brands || [],
          companies: brand.companies || [],
          team: team.data || [],
          regions: insight.regions || [],
          agents: insight.agents || [],
          assignments: assignment.data || [],
          quality: quality.data || [],
          territory: { areas: territory.areas || [], activity: territory.activity || [] },
          topics: topics.topics || [],
          latestDate: topics.latest_date,
          methodNote: topics.method_note,
          topicsTruncated: topics.truncated,
        });
        if (results.some((result) => result.status === 'rejected'))
          setError(
            'Some comparisons could not be loaded. Refresh or adjust the selected period; an empty section may not mean no evidence.',
          );
      })
      .finally(() => {
        if (active) {
          setBusy(false);
          setLoading?.(false);
        }
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [filters, setLoading]);

  const brands = [...data.brands].sort((a, b) => number(b.total) - number(a.total));
  const brand = brands.find((x) => x.brand === brandName) || brands[0];
  const comparison =
    brands.find((x) => x.brand === compareName && x.brand !== brand?.brand) ||
    brands.find((x) => x.brand !== brand?.brand);
  const companies = useMemo(
    () =>
      Object.values(
        data.companies.reduce((all, row) => {
          const entry =
            all[row.company] ||
            (all[row.company] = { name: row.company, conversations: 0, positive: 0, negative: 0 });
          entry.conversations += number(row.conversations);
          if (row.sentiment === 'positive' || row.sentiment === 'negative')
            entry[row.sentiment] += number(row.conversations);
          return all;
        }, {}),
      ).sort((a, b) => b.conversations - a.conversations),
    [data.companies],
  );
  const agent = data.team.find((x) => String(x.agent_id) === String(agentId)) || data.team[0];
  const assigned = data.assignments.find((x) => String(x.agent_id) === String(agent?.agent_id));
  const localRegion = [...data.agents]
    .filter((x) => String(x.agent_id) === String(agent?.agent_id))
    .sort((a, b) => number(b.conversations) - number(a.conversations))[0]?.region;
  const localIds = new Set(
    data.agents.filter((x) => x.region === localRegion).map((x) => String(x.agent_id)),
  );
  const localPeers = data.team.filter((x) => localIds.has(String(x.agent_id)));
  const teamPeers = assigned?.product_team
    ? data.team.filter((x) =>
        data.assignments.some(
          (a) => String(a.agent_id) === String(x.agent_id) && a.product_team === assigned.product_team,
        ),
      )
    : [];
  const avg = (rows, field) =>
    rows.length ? rows.reduce((sum, row) => sum + number(row[field]), 0) / rows.length : null;
  const metricRows = agent
    ? [
        {
          label: 'Conversations',
          person: number(agent.conversation_count),
          province: avg(localPeers, 'conversation_count'),
          product: avg(teamPeers, 'conversation_count'),
          country: avg(data.team, 'conversation_count'),
        },
        {
          label: 'Topics per conversation',
          person: number(agent.entities_per_conversation),
          province: avg(localPeers, 'entities_per_conversation'),
          product: avg(teamPeers, 'entities_per_conversation'),
          country: avg(data.team, 'entities_per_conversation'),
        },
      ]
    : [];
  const agentQuality = data.quality.find((x) => String(x.agent_id) === String(agent?.agent_id));
  const provinces = [...new Set(data.topics.map((x) => x.province))].sort();
  const scoped = data.topics.filter((x) => !province || x.province === province);
  const supplyTypes = [...new Set(data.topics.filter(isSupplyTopic).map((x) => x.entity_type))].sort();
  const procurement = scoped
    .filter((x) => isSupplyTopic(x) && (topicType === 'all' || x.entity_type === topicType))
    .sort((a, b) => number(b.recent) - number(a.recent))
    .slice(0, 25);
  const strategic = scoped
    .filter((x) => x.topic_group === 'issue')
    .sort(
      (a, b) =>
        number(b.recent_contributors) - number(a.recent_contributors) || number(b.recent) - number(a.recent),
    )
    .slice(0, 25);
  const activity = Object.fromEntries(data.territory.activity.map((x) => [String(x.field_hq_code), x]));
  const scope =
    [
      filters?.date_from && `From ${filters.date_from}`,
      filters?.date_to && `to ${filters.date_to}`,
      !filters?.date_from && filters?.time_range && `Last ${filters.time_range} days`,
      filters?.region_id && `Region ${filters.region_id}`,
      province && `Province ${province}`,
    ]
      .filter(Boolean)
      .join(' ') || 'Current dashboard scope';
  const clip = (title, rows, extra = {}) => {
    const item = { page: 'Analytics', title, visual: 'table', scope, rows, ...extra };
    if (title === 'Brand discussion comparison')
      return {
        ...item,
        visual: 'multi_timeline',
        scope: `${scope} · ${brand?.brand || 'Brand'} vs ${comparison?.brand || 'comparison'}`,
        subtitle: 'Recorded brand conversations by week',
        timeline_series: [brand, comparison]
          .filter(Boolean)
          .map((x) => ({ name: x.brand, points: x.timeline || [] })),
        finding:
          brand && comparison
            ? `${brand.brand} was named in ${brand.total} scored conversations; ${comparison.brand} in ${comparison.total}. The lines show when each was discussed, not sales or market share.`
            : 'Two brands are needed for comparison.',
        next_check: 'Read the comments behind any sustained change in discussion or sentiment.',
      };
    if (title === 'Company mentions')
      return {
        ...item,
        visual: 'bars',
        finding: companies.length
          ? `${companies[0].name} was the most-mentioned company in this selection (${companies[0].conversations} conversations). Company mentions are not market share.`
          : 'No company mentions in this selection.',
      };
    if (title === 'Field team peer comparison')
      return {
        ...item,
        scope: `${scope} · ${agent?.agent_name || 'Field team member'}`,
        finding: agent
          ? `${agent.agent_name} recorded ${agent.conversation_count} conversations in this selection. The comparison includes province, product-team and national averages.`
          : 'No field team conversations in this selection.',
        next_check: 'Compare activity with assigned coverage and working days before using it for coaching.',
      };
    if (title === 'Field area contribution')
      return {
        ...item,
        finding:
          'Recorded contribution is grouped by onboarding field-area assignment. No recording does not establish that no visit occurred.',
      };
    if (title === 'Products and ingredients discussed')
      return {
        ...item,
        scope: `${scope} · ${topicType === 'all' ? 'All product types' : topicType}`,
        finding:
          'These are products and ingredients named in conversations. Mentions do not establish orders, demand or stock levels.',
        next_check: 'Check source comments and inventory before a supply decision.',
      };
    if (title === 'Issue discussion by province')
      return {
        ...item,
        finding:
          'The table compares named issues by recent conversations and independent field contributors. It does not measure outbreak incidence.',
        next_check: 'Review comments and field coverage before escalating an issue.',
      };
    return item;
  };

  return (
    <div className="analytics-page v2-page">
      <div className="module-header">
        <div className="module-kicker">DEEPER COMPARISONS</div>
        <h2>Analytics</h2>
        <p>
          Compare recorded market evidence for a specific team decision. Numbers here are not sales, stock or
          farm-incidence measures.
        </p>
      </div>
      <FilterBar />
      <nav className="analytics-tabs" aria-label="Analytics sections">
        {[
          ['marketing', 'Marketing'],
          ['sales', 'Sales'],
          ['procurement', 'Procurement'],
          ['strategy', 'Strategy'],
        ].map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={tab === id ? 'active' : ''}
            onClick={() => {
              setTab(id);
              setSelectedTopic(null);
            }}
          >
            {label}
          </button>
        ))}
      </nav>
      {error && (
        <p className="analytics-warning" role="status">
          {error}
        </p>
      )}
      {busy ? (
        <p className="analytics-empty">Loading comparisons…</p>
      ) : (
        <>
          {data.topicsTruncated && (
            <p className="analytics-warning">
              More topics are recorded than this view can display. Narrow the period or province before
              interpreting rankings.
            </p>
          )}
          {tab === 'marketing' && (
            <>
              <div className="analytics-subtabs">
                <button
                  className={marketingView === 'compare' ? 'active' : ''}
                  onClick={() => setMarketingView('compare')}
                >
                  Brand & company comparison
                </button>
                <button
                  className={marketingView === 'campaigns' ? 'active' : ''}
                  onClick={() => setMarketingView('campaigns')}
                >
                  Campaign plans
                </button>
              </div>
              {marketingView === 'campaigns' ? (
                <CampaignPanel />
              ) : (
                <>
                  <Section
                    title="Compare brand discussion"
                    description="Conversation mentions and AI-scored feedback—not sales or market share."
                    clip={clip(
                      'Brand discussion comparison',
                      [brand, comparison].filter(Boolean).map((x) => ({
                        label: x.brand,
                        value: `${x.total} scored conversations · net sentiment ${x.net_sentiment}%`,
                        status: 'none',
                      })),
                    )}
                  >
                    <div className="analytics-selectors">
                      <label>
                        Brand
                        <select value={brand?.brand || ''} onChange={(e) => setBrandName(e.target.value)}>
                          {brands.map((x) => (
                            <option key={x.brand}>{x.brand}</option>
                          ))}
                        </select>
                      </label>
                      <label>
                        Compare with
                        <select
                          value={comparison?.brand || ''}
                          onChange={(e) => setCompareName(e.target.value)}
                        >
                          {brands
                            .filter((x) => x.brand !== brand?.brand)
                            .map((x) => (
                              <option key={x.brand}>{x.brand}</option>
                            ))}
                        </select>
                      </label>
                    </div>
                    {brand && comparison ? (
                      <>
                        <div className="analytics-two">
                          <div>
                            <strong>{brand.brand}</strong>
                            <span>
                              {brand.total} scored conversations · net sentiment {brand.net_sentiment}%
                            </span>
                            <small>Main co-mentioned crop: {brand.main_context}</small>
                          </div>
                          <div>
                            <strong>{comparison.brand}</strong>
                            <span>
                              {comparison.total} scored conversations · net sentiment{' '}
                              {comparison.net_sentiment}%
                            </span>
                            <small>Main co-mentioned crop: {comparison.main_context}</small>
                          </div>
                        </div>
                        <Plot
                          data={[brand, comparison].map((x, i) => ({
                            type: 'scatter',
                            mode: 'lines+markers',
                            name: x.brand,
                            x: (x.timeline || []).map((y) => y.period),
                            y: (x.timeline || []).map((y) => number(y.conversations)),
                            line: { color: i ? '#c58a20' : '#225caa' },
                          }))}
                          layout={{
                            height: 280,
                            autosize: true,
                            margin: { l: 45, r: 15, t: 10, b: 45 },
                            yaxis: { title: 'Conversations', rangemode: 'tozero' },
                            legend: { orientation: 'h', y: -0.25 },
                            paper_bgcolor: 'transparent',
                            plot_bgcolor: 'transparent',
                          }}
                          config={{ displayModeBar: false, responsive: true }}
                          useResizeHandler
                          style={{ width: '100%' }}
                        />
                      </>
                    ) : (
                      <p className="analytics-empty">Two scored brands are needed for comparison.</p>
                    )}
                  </Section>
                  <Section
                    title="Companies customers named"
                    description="Company-level mentions. A co-mentioned product is not assigned to a company without a verified product master."
                    clip={clip(
                      'Company mentions',
                      companies.slice(0, 10).map((x) => ({
                        label: x.name,
                        value: `${x.conversations} conversations`,
                        status: 'none',
                      })),
                    )}
                  >
                    <div className="analytics-company-list">
                      {companies.slice(0, 10).map((x) => (
                        <div key={x.name}>
                          <b>{x.name}</b>
                          <i>
                            <span
                              style={{
                                width: `${Math.max(3, (100 * x.conversations) / Math.max(1, companies[0]?.conversations))}%`,
                              }}
                            />
                          </i>
                          <strong>{x.conversations}</strong>
                          <small>
                            {x.positive} positive · {x.negative} negative
                          </small>
                        </div>
                      ))}
                      {!companies.length && <p>No company mentions in this selection.</p>}
                    </div>
                  </Section>
                </>
              )}
            </>
          )}
          {tab === 'sales' && (
            <>
              <Section
                title="Compare a field team member fairly"
                description="Compare recorded contribution with people in the same recorded province, assigned product team and country. Counts are not quality scores."
                clip={clip(
                  'Field team peer comparison',
                  metricRows.map((x) => ({
                    label: x.label,
                    value: `Individual ${x.person.toFixed(1)} · province ${x.province?.toFixed(1) || '—'} · country ${x.country?.toFixed(1) || '—'}`,
                    status: 'none',
                  })),
                )}
              >
                <div className="analytics-selectors">
                  <label>
                    Field team member
                    <select value={agent?.agent_id || ''} onChange={(e) => setAgentId(e.target.value)}>
                      {data.team.map((x) => (
                        <option key={x.agent_id} value={x.agent_id}>
                          {x.agent_name}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                {agent ? (
                  <>
                    <p className="analytics-context">
                      {localRegion || 'Recorded province not available'} ·{' '}
                      {assigned?.product_team || 'Product team not assigned'} ·{' '}
                      {agentQuality
                        ? `Latest reviewed quality score ${score(agentQuality.overall_agent_score)}`
                        : 'No reviewed quality score'}
                    </p>
                    <div className="analytics-peer-grid">
                      {metricRows.map((row) => (
                        <div key={row.label}>
                          <b>{row.label}</b>
                          <span>
                            Individual <strong>{row.person.toFixed(1)}</strong>
                          </span>
                          <span>
                            Province average <strong>{row.province?.toFixed(1) || '—'}</strong>
                          </span>
                          <span>
                            Product-team average <strong>{row.product?.toFixed(1) || '—'}</strong>
                          </span>
                          <span>
                            National average <strong>{row.country?.toFixed(1) || '—'}</strong>
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <p className="analytics-empty">No field team conversations in this selection.</p>
                )}
              </Section>
              <Section
                title="Contribution by assigned field area"
                description="Onboarding assignments and recorded conversations are shown together; no recording is not proof of no visit."
                clip={clip(
                  'Field area contribution',
                  (data.territory.areas || []).map((x) => ({
                    label: x.field_hq_name,
                    value: `${activity[String(x.field_hq_code)]?.conversations || 0} conversations · ${activity[String(x.field_hq_code)]?.contributors || 0} contributors`,
                    status: 'none',
                  })),
                )}
              >
                <div className="analytics-table-wrap">
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>Field area</th>
                        <th>Assigned staff</th>
                        <th>Contributing staff</th>
                        <th>Conversations</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.territory.areas || []).map((x) => (
                        <tr key={x.field_hq_code}>
                          <td>{x.field_hq_name}</td>
                          <td>{x.assigned_agents}</td>
                          <td>{activity[String(x.field_hq_code)]?.contributors || 0}</td>
                          <td>{activity[String(x.field_hq_code)]?.conversations || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Section>
            </>
          )}
          {tab === 'procurement' && (
            <>
              <Section
                title="Products and ingredients being discussed"
                description="A lead for supply-team checks. Mention frequency does not measure orders, availability, stock-outs or purchasing intent."
                clip={clip(
                  'Products and ingredients discussed',
                  procurement.slice(0, 10).map((x) => ({
                    label: `${x.entity_name} · ${x.province}`,
                    value: `${x.conversations} conversations · ${x.contributors} field staff`,
                    status: 'none',
                  })),
                )}
              >
                <div className="analytics-selectors">
                  <label>
                    Province
                    <select
                      value={province}
                      onChange={(e) => {
                        setProvince(e.target.value);
                        setSelectedTopic(null);
                      }}
                    >
                      <option value="">All provinces</option>
                      {provinces.map((x) => (
                        <option key={x}>{x}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Topic type
                    <select value={topicType} onChange={(e) => setTopicType(e.target.value)}>
                      <option value="all">Products and ingredients</option>
                      {supplyTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <TopicTable rows={procurement} selected={selectedTopic} onSelect={setSelectedTopic} />
              </Section>
              {selectedTopic && (
                <SignalIntelligencePanel
                  embedded
                  fullWidth
                  entityName={selectedTopic.entity_name}
                  filters={{ ...filters, province: selectedTopic.province }}
                />
              )}
            </>
          )}
          {tab === 'strategy' && (
            <>
              <Section
                title="Issues to watch across regions"
                description="Verified pest, disease and plant-condition names. Compare mention rates and independent field staff; crop links still require evidence review."
                clip={clip(
                  'Issue discussion by province',
                  strategic.slice(0, 10).map((x) => ({
                    label: `${x.entity_name} · ${x.province}`,
                    value: `${x.recent} recent conversations · ${x.recent_contributors} field staff`,
                    status: 'none',
                  })),
                )}
              >
                <div className="analytics-selectors">
                  <label>
                    Province
                    <select
                      value={province}
                      onChange={(e) => {
                        setProvince(e.target.value);
                        setSelectedTopic(null);
                      }}
                    >
                      <option value="">All provinces</option>
                      {provinces.map((x) => (
                        <option key={x}>{x}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <TopicTable
                  rows={strategic}
                  selected={selectedTopic}
                  onSelect={setSelectedTopic}
                  label="Issue named"
                />
                <p className="analytics-method">
                  A higher rate means the issue was named in more of the recorded conversations. It does not
                  establish farm incidence or an outbreak.
                </p>
              </Section>
              {selectedTopic && (
                <SignalIntelligencePanel
                  embedded
                  fullWidth
                  entityName={selectedTopic.entity_name}
                  filters={{ ...filters, province: selectedTopic.province }}
                />
              )}
            </>
          )}
          <p className="analytics-method">
            {data.methodNote || 'Comparisons use recorded FieldForce conversations.'}{' '}
            {data.latestDate ? `Latest conversation: ${data.latestDate}.` : ''} AI sentiment scores and their
            evidence remain stored in the database.
          </p>
        </>
      )}
    </div>
  );
}
